/**
 * @fileoverview Task panel navigation context (MOR-696)
 *
 * Purpose: Lets nested side-panel sections (dependencies, subtasks, parent)
 * push a related task into the side-panel stack so it opens as a new column
 * beside the current one. The side panel provides the implementation; sections
 * consume it via `useTaskPanelNav()`.
 */

import { createContext, useContext } from "react";

import type { CalendarEventRow } from "@/calendar/eventAdapter";

export interface TaskPanelNav {
  /** Open a related task as a new column to the right of the current panel. */
  openRelated: (row: CalendarEventRow) => void;
  /**
   * Open a blank "create task" column pre-parented to `parent`, so a subtask
   * can be authored with all its details before it is saved.
   */
  openCreateChild: (parent: CalendarEventRow) => void;
}

const TaskPanelNavContext = createContext<TaskPanelNav | null>(null);

export const TaskPanelNavProvider = TaskPanelNavContext.Provider;

/** Returns the nav helper, or `null` when rendered outside a stack-aware panel. */
export function useTaskPanelNav(): TaskPanelNav | null {
  return useContext(TaskPanelNavContext);
}
